import * as React from 'react';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import PropTypes from 'prop-types';
import Select from '@mui/material/Select';
import { InputLabel } from '@mui/material';

// Возвращает временной сдвиг для часового пояса loc в формате ±HH:mm
const getTimezoneOffset = (date, loc) => {
  // Используем en, чтобы получить сдвиг. Для аббревиатур используем fr
  let offset;
  ['en', 'fr'].some((lang) => {
    const parts = new Intl.DateTimeFormat(lang, {
      minute: 'numeric',
      timeZone: loc,
      timeZoneName: 'short'
    }).formatToParts(date);
    const tzName = parts.find((part) => part.type === 'timeZoneName' && part.value);
    // timeZoneName с GMT или UTC содержит временной сдвиг - сохраняем и останавливаемся
    // В противном случае это аббревиатура, продолжаем цикл
    if (/^(GMT|UTC)/.test(tzName.value)) {
      offset = tzName.value.replace(/GMT|UTC/, '') || '+0';
      return true;
    }
    return false;
  });
  const isPositive = offset[0] === '\x2b';
  const sign = isPositive ? '\x2b' : '\x2d';
  const [h, m] = offset.substring(1).split(':');
  const shiftMinutes = (isPositive ? 1 : -1) * (+h * 60 + +(m || 0));
  return {
    shiftStr: shiftMinutes === 0 ? '00:00' : `${sign + h.padStart(2, '0')}:${m?.padStart(2, '0') || '00'}`,
    shiftMinutes,
  };
};

const aryIanaTimeZones = Intl.supportedValuesOf('timeZone');

// Единственные коды в своем часовом поясе не нужно приоретизировать (выберутся по умолчанию)
const SUPPORTED_TIMEZONES_PRIORITY = [
  // РФ:
  'Europe/Moscow',
  'Europe/Kaliningrad',
  'Europe/Kirov',
  'Europe/Simferopol',
  'Europe/Volgograd',
  'Europe/Astrakhan',
  'Europe/Samara',
  'Europe/Saratov',
  'Europe/Ulyanovsk',
  'Asia/Yekaterinburg',
  'Asia/Omsk',
  'Asia/Barnaul',
  'Asia/Krasnoyarsk',
  'Asia/Novokuznetsk',
  'Asia/Novosibirsk',
  'Asia/Tomsk',
  'Asia/Irkutsk',
  'Asia/Chita',
  'Asia/Khandyga',
  'Asia/Yakutsk',
  'Asia/Vladivostok',
  'Asia/Ust-Nera',
  'Asia/Magadan',
  'Asia/Sakhalin',
  'Asia/Srednekolymsk',
  'Asia/Anadyr',
  'Asia/Kamchatka',
  // БСССР:
  // 'Europe/Kiev',
  // 'Europe/Minsk',
  // 'Europe/Riga',
  // 'Europe/Tallinn',
  // 'Europe/Vilnius',
  // 'Asia/Baku',
  // 'Asia/Tbilisi',
  // 'Asia/Yerevan',
  // 'Asia/Almaty',
  // 'Asia/Aqtau',
  // 'Asia/Aqtobe',
  // 'Asia/Ashgabat',
  // 'Asia/Atyrau',
  // 'Asia/Dushanbe',
  // 'Asia/Oral',
  // 'Asia/Qostanay',
  // 'Asia/Qyzylorda',
  // 'Asia/Samarkand',
  // 'Asia/Tashkent',
  // 'Asia/Bishkek',
  // Загран:
  'Pacific/Midway',
  'Pacific/Honolulu',
  // 'Pacific/Marquesas',
  'America/Anchorage',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Chicago',
  'America/New_York',
  'America/Caracas',
  // 'America/St_Johns',
  'America/Buenos_Aires',
  'America/Godthab',
  'Atlantic/Cape_Verde',
  'Europe/London',
  'Europe/Paris',
  'Asia/Tehran',
  'Asia/Kabul',
  'Asia/Calcutta',
  // 'Asia/Katmandu',
  'Asia/Rangoon',
  // 'Australia/Eucla',
  // 'Australia/Darwin',
  'Australia/Adelaide',
  'Pacific/Auckland',
  // 'Pacific/Chatham',
  // 'Pacific/Kiritimati',
];

export const timeZoneOptions = (() => {
  const d = Date.now();
  const tZones = aryIanaTimeZones
    .map((tZ) => {
      const { shiftStr, shiftMinutes } = getTimezoneOffset(d, tZ);
      return {
        timeZoneName: tZ,
        utcShiftMinutes: shiftMinutes,
        utcShiftStr: shiftStr,
      };
    })
    .reduce((acc, tz) => {
      const existingTz = acc.find(
        (atz) => atz.valueMinutes === tz.utcShiftMinutes
      );
      if (existingTz) {
        existingTz.timeZoneCodes.push(tz.timeZoneName);
      } else {
        acc.push({
          valueMinutes: tz.utcShiftMinutes,
          valueStr: tz.utcShiftStr,
          timeZoneCodes: [tz.timeZoneName],
        });
      }
      return acc;
    }, [])
    .map((item) => {
      const uniqueTimeZoneCodes = [...new Set(item.timeZoneCodes)]
        .reduce((acc, c) => {
          const priority = SUPPORTED_TIMEZONES_PRIORITY.findIndex(
            (code) => code === c
          );
          if (priority !== -1) {
            acc[priority] = c;
          }
          return acc;
        }, [])
        .filter((i) => i);
      const timezoneCodes = uniqueTimeZoneCodes.length
        ? uniqueTimeZoneCodes
        : [item.timeZoneCodes[0]];
      return {
        region: timezoneCodes.join(", "),
        // label: `(UTC ${item.valueStr}) ${timezoneCodes.map(getLocalTZName).join(', ')}`,
        label: `(UTC${item.valueStr}) ${timezoneCodes.join(", ")}`,
        value: item.valueStr,
        timeZoneCodes: [...new Set(item.timeZoneCodes)],
        shiftMinutes: item.valueMinutes,
      };
    })
    .sort((a, b) => a.shiftMinutes - b.shiftMinutes);
  return tZones;
})();

function TimeZoneSelector(props) {
  const {
    disabled,
    value,
    onChange,
    ...restProps
  } = props;

  return (
    <FormControl
      disabled={disabled}
      sx={{ minWidth: 140, width: "100%" }}
      {...restProps}
    >
      <InputLabel id="timeZpne-label">Часовой пояс</InputLabel>
      <Select
        labelId="timeZpne-label"
        id="timeZpne"
        value={value}
        onChange={onChange}
        label="Часовой пояс"
      >
        {timeZoneOptions.map((size) => (
          <MenuItem value={size.value}>{size.label}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

TimeZoneSelector.propTypes = {
  disabled: PropTypes.bool,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

TimeZoneSelector.defaultProps = {
  disabled: false,
};

export default TimeZoneSelector;
